// Above this many word pairs the LCS table is too big to build on a phone;
// the view falls back to showing the new text without highlights.
export const MAX_DIFF_CELLS = 600000

const wordsOf = text => (text || '').match(/\S+/g) || []

// The new text's words with the whitespace that precedes each one, so the
// rendered comparison keeps the story's paragraph breaks and spacing.
const spacedWordsOf = text => {
  const words = []
  const pattern = /(\s*)(\S+)/g
  let match

  while ((match = pattern.exec(text || '')) !== null) words.push({sep: match[1], text: match[2]})

  return words
}

/** Whether the two texts are small enough to be compared word by word. */
export const canHighlight = (before, after) => wordsOf(before).length * wordsOf(after).length <= MAX_DIFF_CELLS

/**
 * Word-level diff of two texts (TK-3204 "Show changes", AC 5.7), as tokens
 * `{type: 'same' | 'ins' | 'del', text, sep}` in reading order, where `sep`
 * is the whitespace to print before the token: the new text's own for kept
 * and added words (so its line and paragraph breaks survive), a space for
 * removed ones. Consecutive removed and added words are grouped so the view
 * shows "old phrase" struck through followed by "new phrase" highlighted.
 * Rendered as text nodes only: never turned back into HTML.
 */
const wordDiff = (before, after) => {
  const a = wordsOf(before)
  const b = spacedWordsOf(after)
  const n = a.length
  const m = b.length

  if (n * m > MAX_DIFF_CELLS) return m ? [{type: 'same', text: (after || '').trim(), sep: ''}] : []

  // dp[i][j] = length of the longest common subsequence of a[i..] and b[j..]
  const dp = new Array(n + 1)

  for (let i = 0; i <= n; i++) dp[i] = new Uint16Array(m + 1)

  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--)
      dp[i][j] = a[i] === b[j].text ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1])
  }

  const tokens = []
  let inserted = []
  let deleted = []

  // Removed words print a space before them, unless the new text's next word
  // starts a new line or paragraph: then the break comes first and the struck
  // phrase sits at the start of that paragraph, beside what replaced it,
  // instead of dangling at the end of the previous one.
  // Returns whether the break was taken from `next` (a kept word), which then
  // prints after a plain space.
  const flush = next => {
    const following = inserted.length ? inserted[0] : next
    const lead = following ? following.sep : ''
    const breakFirst = deleted.length > 0 && lead.includes('\n')

    if (deleted.length) tokens.push({type: 'del', text: deleted.join(' '), sep: breakFirst ? lead : ' '})

    if (inserted.length) {
      const text = inserted.map((word, index) => (index ? word.sep || ' ' : '') + word.text).join('')

      tokens.push({type: 'ins', text, sep: breakFirst ? ' ' : inserted[0].sep})
    }

    const takenFromNext = breakFirst && !inserted.length

    inserted = []
    deleted = []

    return takenFromNext
  }

  let i = 0
  let j = 0

  while (i < n || j < m) {
    if (i < n && j < m && a[i] === b[j].text) {
      const breakTaken = flush(b[j])

      tokens.push({type: 'same', text: b[j].text, sep: breakTaken ? ' ' : b[j].sep})
      i++
      j++
    } else if (j < m && (i >= n || dp[i][j + 1] >= dp[i + 1][j])) {
      inserted.push(b[j++])
    } else {
      deleted.push(a[i++])
    }
  }

  flush()

  return tokens
}

export default wordDiff
